// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Wind maths, kept pure so the one thing that is easy to get backwards — meteorological
// direction — is asserted rather than eyeballed.

import type { DistanceUnit } from '@/features/units/distance';

import type { LatLon } from './tileMath';

const MPH_PER_MPS = 2.2369363;

/**
 * Below this, direction stops meaning anything: a 0.5 m/s reading swings through the whole
 * compass between updates, and no golfer plays for it.
 */
export const CALM_MPS = 1;

export type Wind = {
  speedMps: number;
  /**
   * Where the wind blows FROM, degrees clockwise from north — the meteorological
   * convention every weather API reports, and the opposite of the way an arrow points.
   */
  fromDegrees: number;
  gustMps: number | null;
};

export type WindDescriptor = 'calm' | 'helping' | 'into' | 'leftToRight' | 'rightToLeft';

export type RelativeWind = {
  /** Positive helping, negative into the shot. */
  alongMps: number;
  /** Positive blowing toward the right of the line of play. */
  crossMps: number;
  descriptor: WindDescriptor;
  /** Degrees clockwise from north that the wind blows TOWARD, which is where an arrow points. */
  towardDegrees: number;
};

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

/** Normalises to [0, 360). */
export const normalizeBearing = (degrees: number): number => ((degrees % 360) + 360) % 360;

/** Initial great-circle bearing from one point to another, degrees clockwise from north. */
export const bearingDegrees = (from: LatLon, to: LatLon): number => {
  const dLon = toRad(to.lon - from.lon);
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return normalizeBearing(toDeg(Math.atan2(y, x)));
};

/**
 * Splits the wind into what a golfer actually plays: the part along the shot and the part
 * across it.
 *
 * `holeBearing` is tee to green. The along component is positive when the wind pushes the
 * ball toward the green, and the cross component is positive when it pushes it to the right
 * of that line — "off the left", in the way it is usually said out loud.
 */
export const relativeWind = (wind: Wind, holeBearing: number): RelativeWind => {
  const towardDegrees = normalizeBearing(wind.fromDegrees + 180);
  const delta = toRad(towardDegrees - holeBearing);
  const alongMps = wind.speedMps * Math.cos(delta);
  const crossMps = wind.speedMps * Math.sin(delta);

  let descriptor: WindDescriptor;
  if (wind.speedMps < CALM_MPS) {
    descriptor = 'calm';
  } else if (Math.abs(alongMps) >= Math.abs(crossMps)) {
    descriptor = alongMps > 0 ? 'helping' : 'into';
  } else {
    descriptor = crossMps > 0 ? 'leftToRight' : 'rightToLeft';
  }

  return { alongMps, crossMps, descriptor, towardDegrees };
};

/** Wind speed in the unit the player reads distances in: m/s metric, mph imperial. */
export const formatWindSpeed = (mps: number, unit: DistanceUnit): string => {
  if (!Number.isFinite(mps)) return '';
  return unit === 'imperial'
    ? `${Math.round(mps * MPH_PER_MPS)} mph`
    : `${Math.round(mps)} m/s`;
};

/** The eight-point compass name of a bearing, for the "from" readout. */
export const compassPoint = (
  degrees: number
): 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw' => {
  const points = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'] as const;
  return points[Math.round(normalizeBearing(degrees) / 45) % 8];
};
